// 5.33.0 Calendar "Side by side" — pure helpers (no React), unit-tested in calendarPanes.test.js.
// A pane option comes from GET /api/calendar/panes: {key, user, owner, group, calendar}. `user` is null for the
// login's own view and a username only for an admin looking at another person's Personal.

export const VIEWS = [
  ['dayGridMonth', 'Month'], ['timeGridWeek', 'Week'], ['timeGridDay', 'Day'], ['listWeek', 'List'],
];
export const DEFAULT_VIEW = 'dayGridMonth';
// 5.37.0: Overlay is the default and comes first; Quad = 4 consecutive months of one calendar, Year = 12 months.
export const LAYOUTS = [['overlay', 'Overlay'], ['side', 'Side by side'], ['quad', 'Quad'], ['year', 'Year']];
export const LAYOUT_ICON = { overlay: '▣', side: '◫', quad: '⊞', year: '▦' };
const GROUP_ORDER = ['Mine', 'Shared with me', 'Office', 'People'];
// 5.37.1 colours: HDCal is the one source — a calendar's own colour (calendar.color, the Apple calendar-color the sync
// reads; Office deep purple, Harry's Personal green) wins. Calendars without one fall back to this palette, which has
// no green / purple left so nothing can be mistaken for those two.
export const FALLBACK_SHARED = ['#f59e0b', '#ec4899', '#0ea5e9', '#eab308', '#f43f5e'];
export const FALLBACK_KIND = { personal: '#3b82f6', office: '#6b21a8', other: '#06b6d4' };
const hexColor = (c) => (/^#[0-9a-fA-F]{6}$/.test(c || '') ? c : null);

// Colour per calendar (id -> colour) for a list of calendars: own colour, else palette (shared ones take turns, id order).
export function calendarColors(calendars) {
  const out = {};
  let s = 0;
  [...calendars].sort((a, b) => a.id - b.id).forEach((c) => {
    out[c.id] = hexColor(c.color) || (c.kind === 'shared' ? FALLBACK_SHARED[s++ % FALLBACK_SHARED.length] : (FALLBACK_KIND[c.kind] || '#64748b'));
  });
  return out;
}

// Same colours per pane option key (Side by side, Quad, Year, the Overlay legend).
export function paneColors(options) {
  const out = {};
  const own = calendarColors(options.filter((o) => !o.user).map((o) => o.calendar));
  options.forEach((o) => {
    out[o.key] = o.user ? (hexColor(o.calendar.color) || FALLBACK_KIND[o.calendar.kind] || '#64748b') : own[o.calendar.id];
  });
  return out;
}

export function optionLabel(o) {
  if (!o) return '';
  if (o.owner) return `${o.owner} – ${o.calendar.name}`;
  if (o.key === 'mine') return `My calendar (${o.calendar.name})`;
  return o.calendar.name;
}

export function groupOptions(options) {
  return GROUP_ORDER.map((g) => [g, options.filter((o) => o.group === g)]).filter(([, list]) => list.length);
}

// Calendars offered in "New event" from a pane: the pane's own calendar first (when writable), then the other
// calendars that work in the SAME view (own view: my Personal + Office; a person's view: Office).
export function createChoicesFor(option, options) {
  if (!option) return [];
  const out = [];
  const add = (c) => { if (c && c.writable && (c.components || ['VEVENT']).includes('VEVENT') && !out.some((x) => x.id === c.id)) out.push(c); };
  add(option.calendar);
  if (!option.user) add((options.find((o) => o.key === 'mine') || {}).calendar);
  add((options.find((o) => o.key === 'office') || {}).calendar);
  return out;
}

// Which panes to draw: [left, right], or [right] alone when left is '' / unknown.
export function visiblePanes(options, left, right) {
  const byKey = Object.fromEntries(options.map((o) => [o.key, o]));
  const r = byKey[right] || byKey.office || options[0] || null;
  const l = left ? byKey[left] || null : null;
  return [l, r].filter(Boolean);
}

// ---------------------------------------------------------------------------------------------------------------
// 5.36.0 independent panes + fitting on 1080p.
// Dates here are FullCalendar's UTC-coded Chicago wall dates (calendarTime.js): only the UTC fields are used.

export const monthIndex = (d) => d.getUTCFullYear() * 12 + d.getUTCMonth();

// The 1st of the month `n` months after `d`'s month.
export function addMonthsFirst(d, n) {
  const m = monthIndex(d) + n;
  return new Date(Date.UTC(Math.floor(m / 12), ((m % 12) + 12) % 12, 1));
}

// Months from the left pane's date to the right pane's date (what is remembered, never the dates themselves).
export const monthOffset = (leftDate, rightDate) => monthIndex(rightDate) - monthIndex(leftDate);

// Where the right pane opens: the left pane's date when offset is 0, else the 1st of left month + offset.
export const rightStartDate = (leftDate, offset) => (offset ? addMonthsFirst(leftDate, offset) : leftDate);

export const OFFSET_MAX = 24;                      // same bound as the server (panes.OFFSET_MAX)
export const clampOffset = (n) => Math.max(-OFFSET_MAX, Math.min(OFFSET_MAX, n));

// Short event times: 9a, 9:30a, 2p, 12p; a range "9a–10:30a" (Week / Day views).
export function shortTime(hour, minute) {
  const h = hour % 12 || 12;
  return `${h}${minute ? `:${String(minute).padStart(2, '0')}` : ''}${hour < 12 ? 'a' : 'p'}`;
}
export function shortTimeFormat(arg) {
  const s = shortTime(arg.start.hour, arg.start.minute);
  return arg.end ? `${s}–${shortTime(arg.end.hour, arg.end.minute)}` : s;
}

// Text size + "show all events" are remembered PER COMPUTER (this browser's storage), not per login: a 4K screen
// and a 1080p office PC each keep their own. Storage may be missing / blocked — then the defaults apply.
export const TEXT_SIZES = [['s', 'Small', 12], ['m', 'Medium', 13], ['l', 'Large', 15], ['xl', 'Extra large', 17]];
export const DEFAULT_TEXT_SIZE = 'm';
export const TEXT_SIZE_KEY = 'insuredocs.calendar.textSize.v1';
export const SHOW_ALL_KEY = 'insuredocs.calendar.showAll.v1';
export const textSizePx = (k) => (TEXT_SIZES.find((t) => t[0] === k) || TEXT_SIZES[1])[2];

export function readDisplayPrefs() {
  let size = DEFAULT_TEXT_SIZE;
  let showAll = true;
  try {
    const s = window.localStorage.getItem(TEXT_SIZE_KEY);
    if (TEXT_SIZES.some((t) => t[0] === s)) size = s;
    if (window.localStorage.getItem(SHOW_ALL_KEY) === '0') showAll = false;
  } catch (_e) { /* no storage: defaults */ }
  return { size, showAll };
}

export function writeDisplayPref(key, value) {
  try { window.localStorage.setItem(key, value); } catch (_e) { /* no storage: this visit only */ }
}

// ---------------------------------------------------------------------------------------------------------------
// 5.37.0 Overlay legend, saved per login as {on: [ids], off: [ids]} = ticks over the DEFAULT ticks.
// 5.37.1: the default ticks of the login's own view come from the server (GET /api/calendar/panes → legend.default_on:
// admins with a personal calendar except x1001 = everything; others own Personal + Office; no Personal = Office).
// A calendar with no saved tick follows the default, so one that appears later does too.
// legendDefaultIds() is the rule for another person's view (admin person picker): their Personal + Office.
export const EMPTY_LEGEND = { on: [], off: [] };

export function legendDefaultIds(calendars) {
  const p = calendars.find((x) => x.kind === 'personal');
  const o = calendars.find((x) => x.kind === 'office');
  return [p, o].filter(Boolean).map((c) => c.id);
}

export function legendShown(legend, id, defaultIds) {
  const lg = legend || EMPTY_LEGEND;
  if (lg.on.includes(id)) return true;
  if (lg.off.includes(id)) return false;
  return (defaultIds || []).includes(id);
}

// Flip one calendar; keeps the stored lists minimal (a calendar is only listed when it differs from the default).
export function toggleLegend(legend, id, defaultIds) {
  const lg = legend || EMPTY_LEGEND;
  const isDefault = (defaultIds || []).includes(id);
  const shown = legendShown(lg, id, defaultIds);
  const on = lg.on.filter((x) => x !== id);
  const off = lg.off.filter((x) => x !== id);
  if (shown && isDefault) off.push(id);
  if (!shown && !isDefault) on.push(id);
  return { on: on.sort((a, b) => a - b), off: off.sort((a, b) => a - b) };
}
